import { Point, Rectangle } from 'pixi.js';
import { TextAlignment, DynamicLevel } from '../../../../enums';
import type { CanvasTextStyleRenderer } from '../renderer/CanvasTextStyleRenderer';
import { isBreakingSpace, isNewline, metricsContext, tokenize } from '../textMetricsHelpers';
import type { Transcript } from '../../../Transcript';
import type { AnimatedValue } from '../renderer/CanvasTextDynamicAnimationBase';
import { CanvasWordMetrics } from './CanvasWordMetrics';
import { CanvasWordElementMetrics, getStyleForMetrics } from './CanvasWordElementMetrics';
import { CanvasTextLineMetrics } from './CanvasTextLineMetrics';
import { CanvasWordToken } from './CanvasWordToken';
import { CanvasTextMetricList } from './CanvasTextMetricList';
import type { CanvasHighlightSpanMetrics } from './CanvasHighlightSpanMetrics';

type LargestWordElementsType = {
    ascent: {
        text: string;
        value: number;
    };
    descent: {
        text: string;
        value: number;
    };
};

type CharOffsets = {
    /**
     * index of the line
     */
    lineIndex: number;
    /**
     * index of the word into the selected line
     */
    wordIndex: number;
    /**
     * index of the word element into the selected word
     */
    elementIndex: number;
    /**
     * index of the character into the selected element
     */
    characterIndex: number;
};

/**
 * ROUNDING_PRECISION to RANGE_TOLERANCE relationship:
 * - 0 = 0
 * - 1 = 0.1
 * - 2 = 0.01
 * Both values should always be changed in tandem.
 */
export const ROUNDING_PRECISION: number = 1;

/**
 * CanvasTextMetrics is a class that represents the measurement of a block of text. It is broken down in lines, words
 * and word elements.
 */
export class CanvasTextMetrics extends CanvasTextMetricList<CanvasTextLineMetrics> {
    #canDisplayAllText = true;
    #objectToWordElementIndexArray: number[] = null;
    #lineIndexToWordElementIndexArray: number[] = null;
    #wordIndexToWordElementIndexArray: number[] = null;
    #highlightSpanIndexToWordElementIndexArray: number[] = null;
    #wordMetricsWithoutBreakingSpaces: CanvasWordMetrics[] = null;
    #wordMetricsWithBreakingSpaces: CanvasWordMetrics[] = null;
    #highlightSpans: CanvasHighlightSpanMetrics[] = null;

    constructor(children: Array<CanvasTextLineMetrics>, canDisplayAllText = true) {
        super(children);
        this.#canDisplayAllText = canDisplayAllText;
    }

    override clearCachedValues() {
        super.clearCachedValues();
        this.#objectToWordElementIndexArray = null;
        this.#lineIndexToWordElementIndexArray = null;
        this.#wordIndexToWordElementIndexArray = null;
        this.#highlightSpanIndexToWordElementIndexArray = null;
    }

    get lines(): CanvasTextLineMetrics[] {
        return this.children;
    }

    get area(): number {
        return this.width * this.height;
    }

    override _getBounds(): Rectangle {
        if (this.lines.length === 0) {
            return Rectangle.EMPTY;
        }
        const box = this.lines[0].bounds.clone();
        for (let i = 1; i < this.lines.length; i++) {
            const bounds = this.lines[i].bounds;
            box.enlarge(bounds);
        }

        return box;
    }

    /**
     * @return Just the words, excluding breaking spaces.
     */
    get wordMetricsWithoutSpaces(): CanvasWordMetrics[] {
        if (!this.#wordMetricsWithoutBreakingSpaces) {
            this.#wordMetricsWithoutBreakingSpaces = this.lines
                .flatMap((line) => line.words)
                .filter((word) => !isBreakingSpace(word.text));
        }
        return this.#wordMetricsWithoutBreakingSpaces;
    }

    /**
     * @return All the word metrics, including breaking spaces.
     */
    get wordMetrics(): CanvasWordMetrics[] {
        if (!this.#wordMetricsWithBreakingSpaces) {
            this.#wordMetricsWithBreakingSpaces = this.lines.flatMap((line) => line.words);
        }
        return this.#wordMetricsWithBreakingSpaces;
    }

    get highlightSpans(): CanvasHighlightSpanMetrics[] {
        if (!this.#highlightSpans) {
            this.#highlightSpans = this.lines.flatMap((line) => line.highlightSpans);
        }
        return this.#highlightSpans;
    }

    get baseLineOffsetY() {
        return this.style.fontSize * this.style.lineHeight;
    }

    get maxLineHeight() {
        return this.lines.reduce((value, line) => Math.max(value, line.height), 0);
    }

    get maxLineAscent() {
        return this.lines.reduce((value, line) => Math.max(value, line.ascent), 0);
    }

    get maxLineDescent() {
        return this.lines.reduce((value, line) => Math.max(value, line.descent), 0);
    }

    get maxHighlightSpanAscent() {
        return this.highlightSpans.reduce((value, span) => Math.max(value, span.ascent), 0);
    }

    get maxHighlightSpanDescent() {
        return this.highlightSpans.reduce((value, span) => Math.max(value, span.descent), 0);
    }

    get x() {
        return this.bounds.x;
    }

    get y() {
        return this.bounds.y;
    }

    override _getWidth(): number {
        return this.bounds.width;
    }

    override _getHeight(): number {
        return this.bounds.height;
    }

    get canDisplayAllText() {
        return this.#canDisplayAllText;
    }

    override _getSourceWordReferenceIndices(): number[] {
        return this.lines.flatMap(
            (line) => (line as CanvasTextLineMetrics).sourceWordReferenceIndices,
        );
    }

    /**
     * Spreads the values over the given level. This is used to shape our input values array to match our target level.
     * For instance, if we want to apply the object level values of [0] to all of our word elements then we need to
     * create an array of the same length as our word elements and fill it with the value [0]. If we have metrics with
     * 3 elements then we need to create an array of length 3 and thus fill it with [0, 0, 0].
     * */
    spreadLevelValues(values: number[] | AnimatedValue[], level: DynamicLevel) {
        const callback = (wordElementIndex: number): number => {
            const entry = values[wordElementIndex] ?? -1;
            if (typeof entry === 'object') {
                return entry.value;
            }
            return entry;
        };
        switch (level) {
            case DynamicLevel.object:
                return this.objectToWordElementIndexArray.map(callback);
            case DynamicLevel.line:
                return this.lineIndexToWordElementIndexArray.map(callback);
            case DynamicLevel.word:
                return this.wordIndexToWordElementIndexArray.map(callback);
            case DynamicLevel.highlight:
                return this.highlightSpanIndexToWordElementIndexArray.map(callback);
            default:
                throw new Error(`Unknown level to spread: ${level}`);
        }
    }

    /** Get a mapping between the object and word elements. */
    get objectToWordElementIndexArray() {
        if (this.#objectToWordElementIndexArray === null) {
            this.#objectToWordElementIndexArray = this._getObjectToWordElementIndexArray();
        }
        return this.#objectToWordElementIndexArray;
    }

    _getObjectToWordElementIndexArray(): number[] {
        const array = [];
        this.lines.forEach((line) => {
            line.words.forEach((word) => {
                word.elements.forEach(() => {
                    array.push(0);
                });
            });
        });
        return array;
    }

    /** Get a mapping between the line and word elements. */
    get lineIndexToWordElementIndexArray(): number[] {
        if (this.#lineIndexToWordElementIndexArray === null) {
            this.#lineIndexToWordElementIndexArray = this._getLineIndexToWordElementIndexArray();
        }
        return this.#lineIndexToWordElementIndexArray;
    }

    _getLineIndexToWordElementIndexArray() {
        const array = [];
        this.lines.forEach((line, index) => {
            // we should probably cache the different element counts on each level? Not sure what the performance
            // difference would be.
            line.words.forEach((word) => {
                word.elements.forEach(() => {
                    array.push(index);
                });
            });
        });
        return array;
    }

    /** Get a mapping between the word and word elements. */
    get wordIndexToWordElementIndexArray() {
        if (this.#wordIndexToWordElementIndexArray === null) {
            this.#wordIndexToWordElementIndexArray = this._getWordIndexToWordElementIndexArray();
        }
        return this.#wordIndexToWordElementIndexArray;
    }

    _getWordIndexToWordElementIndexArray() {
        const array = [];
        let valueIndex = 0;
        this.lines.forEach((line) => {
            line.words.forEach((word) => {
                if (isBreakingSpace(word.text)) {
                    array.push(-1);
                    return;
                }
                word.elements.forEach(() => {
                    array.push(valueIndex);
                });
                valueIndex++;
            });
        });
        return array;
    }

    /**
     * Get the line, word, element and element character index for the provided global
     * character index.
     */
    getOffsetsForChar(charIndex: number): CharOffsets {
        let currIndex = 0;
        // Iterate over all lines
        for (let lineIndex = 0; lineIndex < this.lines.length; lineIndex++) {
            const line = this.lines[lineIndex];

            // Handle if a leading space was removed
            if (line.removedLeadingSpace) {
                if (currIndex === charIndex) {
                    return {
                        lineIndex,
                        wordIndex: 0,
                        elementIndex: 0,
                        characterIndex: 0,
                    };
                }
                currIndex++;
            }

            // Iterate over all words in this line
            for (let wordIndex = 0; wordIndex < line.words.length; wordIndex++) {
                const textWord = line.words[wordIndex];

                // Iterate over all word elements in this line
                for (
                    let elementIndex = 0;
                    elementIndex < textWord.elements.length;
                    elementIndex++
                ) {
                    const wordElement = textWord.elements[elementIndex];

                    // Iterate over all characters in this word element
                    for (
                        let characterIndex = 0;
                        characterIndex < wordElement.text.length;
                        characterIndex++
                    ) {
                        if (currIndex === charIndex) {
                            return {
                                lineIndex,
                                wordIndex,
                                elementIndex,
                                characterIndex,
                            };
                        }
                        currIndex++;
                    }
                }
            }

            // Handle if a trailing space was removed
            if (line.removedTrailingSpace) {
                currIndex++;
                if (currIndex === charIndex) {
                    const wordIndex = line.words.length - 1;
                    const elementIndex = line.words[wordIndex].elements.length - 1;
                    const characterIndex =
                        line.words[wordIndex].elements[elementIndex].text.length - 1;
                    return {
                        lineIndex,
                        wordIndex,
                        elementIndex,
                        characterIndex,
                    };
                }
            }
        }

        // Fallback to after the last character
        let lineIndex = 0;
        let wordIndex = 0;
        let elementIndex = 0;
        let characterIndex = 0;
        if (this.lines.length > 0) {
            lineIndex = this.lines.length - 1;
            const numWordsLastLine = this.lines[lineIndex].words.length;
            if (numWordsLastLine > 0) {
                wordIndex = numWordsLastLine - 1;
                const numElementsLastWord = this.lines[lineIndex].words[wordIndex].elements.length;
                if (numElementsLastWord > 0) {
                    elementIndex = numElementsLastWord - 1;
                    characterIndex =
                        this.lines[lineIndex].words[wordIndex].elements[elementIndex].text.length;
                }
            }
        }

        return {
            lineIndex,
            wordIndex,
            elementIndex,
            characterIndex,
        };
    }

    /**
     * Compute the x-distance into a word element that a provided char index is. Note that this
     * distance is from the word element's origin (and not the bounding box origin).
     *
     * @param lineIndex - the line index in which the char is
     * @param wordIndex - the word index into the line in which the char is
     * @param elementIndex - the word element index into the text word in which the char is
     * @param charIndex - the char index into the word element
     * @returns the location of the char relative to the start of its word element
     */
    positionOfCharRelativeToWordElement(
        lineIndex: number,
        wordIndex: number,
        elementIndex: number,
        charIndex: number,
    ): Point {
        const line = this.lines[lineIndex];
        if (!line) {
            return new Point(0, 0);
        }

        const textWord = line.words[wordIndex];
        if (!textWord) {
            return new Point(0, 0);
        }

        const wordElement = textWord.elements[elementIndex];
        if (!wordElement) {
            return new Point(0, 0);
        }

        return new Point(wordElement.partialAdvanceWidth(0, charIndex), 0);
    }

    /** Get a mapping between the highlight spans and word elements. */
    get highlightSpanIndexToWordElementIndexArray(): number[] {
        if (this.#highlightSpanIndexToWordElementIndexArray === null) {
            this.#highlightSpanIndexToWordElementIndexArray =
                this._getHighlightSpanIndexToWordElementIndexArray();
        }
        return this.#highlightSpanIndexToWordElementIndexArray;
    }

    /**
     * For any words not within a highlight span, it will set a value of -1. For words within a highlight span,
     * it will set a value to the index of the highlight span that those words are a part of.
     */
    _getHighlightSpanIndexToWordElementIndexArray() {
        const array = [];
        let currentHighlightIndex = -1;
        let isInHighlight = false;
        this.lines.forEach((line) => {
            // highlights do not cross lines
            isInHighlight = false;
            line.words.forEach((word) => {
                word.elements.forEach((element) => {
                    if (element.highlighted) {
                        if (!isInHighlight) {
                            currentHighlightIndex++;
                            isInHighlight = true;
                        }
                        array.push(currentHighlightIndex);
                    } else {
                        array.push(-1);
                        isInHighlight = false;
                    }
                });
            });
        });
        return array;
    }

    /** Returns the world elements with the largest ascender and descender */
    getTallestWordElements(): LargestWordElementsType {
        // get the word elements with the largest ascent
        let largestAscent = 0;
        let largestAscentText = '';

        // get the word elements with the largest descent
        let largestDescent = 0;
        let largestDescentText = '';

        this.lines.forEach((line) => {
            line.words.forEach((word) => {
                word.elements.forEach((element) => {
                    if (element.ascent > largestAscent) {
                        largestAscent = element.ascent;
                        largestAscentText = element.text;
                    }
                    if (element.descent > largestDescent) {
                        largestDescent = element.descent;
                        largestDescentText = element.text;
                    }
                });
            });
        });

        return {
            ascent: {
                text: largestAscentText,
                value: largestAscent,
            },
            descent: {
                text: largestDescentText,
                value: largestDescent,
            },
        };
    }

    /** Remove trailing punctuations from the last chunk on the last line. */
    removeTrailingPunctuations() {
        if (this.lines.length === 0) {
            return;
        }
        if (this.lines.at(-1).removeEndOfLinePunctuation()) {
            this.clearCachedValues();
        }
    }

    static getChunkTimeRanges(metrics: CanvasTextMetrics[], transcript: Transcript): number[][] {
        if (transcript.empty) {
            return [];
        }
        return metrics.map((chunkMetric) => {
            const [startIndex, endIndex] = chunkMetric.sourceWordReferenceIndexRange;
            return [transcript.words[startIndex].startTime, transcript.words[endIndex].endTime];
        });
    }

    static getChunkTexts(metrics: CanvasTextMetrics[]): string[] {
        return metrics.map((chunkMetric) => chunkMetric.text);
    }
}

/**
 * Sets the alignment and reading direction of each line based on the alignment and the max line width.
 */
export function setAlignmentAndReadingDirection(
    lines: CanvasTextLineMetrics[],
    alignment: TextAlignment,
) {
    let maxLineWidth = 0;
    let minLineX = Infinity;
    lines.forEach((line) => {
        maxLineWidth = Math.max(maxLineWidth, line.bounds.width);
        minLineX = Math.min(minLineX, line.bounds.x);
    });
    lines.forEach((line) => {
        line.setAlignmentAndReadingDirection(minLineX, maxLineWidth, alignment);
    });
}

/**
 * Sets the position of the underlying word elements so that each line of words is positioned below the previous line
 * taking into account the line height.
 */
export function positionLinesBelowEachOther(
    lines: CanvasTextLineMetrics[],
    style: CanvasTextStyleRenderer,
) {
    // https://www.w3.org/TR/CSS22/visudet.html#line-height
    if (lines.length === 0) {
        return;
    }
    const leading = (style.baselineOffset - lines[0].height) * 0.5;
    let yBaseline = lines[0].ascent + leading;
    lines.forEach((line) => {
        line.setPosition(0, yBaseline);
        yBaseline += style.baselineOffset;
    });
}

/**
 * Annotate words with additional information such as highlight status and word reference indices. If a word falls
 * within the provided highlight areas then it will be marked as highlighted. The word reference indices are used to
 * map the word back to the original text. If no indices are provided then the index of the word in the word list will
 * be used.
 */
export function annotateWords(
    words: string[],
    highlightAreas: [number, number][],
    sourceWordReferenceIndices?: number[],
) {
    const annotatedWordTokens = words.map((word, index) => {
        const sourceWordReferenceIndex = sourceWordReferenceIndices
            ? sourceWordReferenceIndices[index]
            : index;
        return new CanvasWordToken(word, false, sourceWordReferenceIndex);
    });

    highlightAreas.forEach((highlight) => {
        // process each highlight pair
        const highlightStart = highlight[0];
        const highlightEnd = highlight[1];
        let charCounter = 0;

        // process every token to apply the highlight
        for (const token of annotatedWordTokens) {
            const tokenStart = charCounter;
            const tokenEnd = tokenStart + token.length;
            if (tokenStart > highlightEnd) {
                // passed the end of the highlight so can skip the rest of the tokens
                break;
            }
            if (highlightStart < tokenEnd && highlightEnd > tokenStart) {
                token.highlight(highlightStart - tokenStart, highlightEnd - tokenStart);
            }
            charCounter += token.length;
        }
    });

    return annotatedWordTokens;
}

/**
 * Creates the initial CanvasWordElementMetrics for each word token based on the assigned text style.
 */
export function createWordMetrics(
    wordTokens: CanvasWordToken[],
    normalStyle: CanvasTextStyleRenderer,
    highlightStyle: CanvasTextStyleRenderer,
) {
    const wordMetrics: CanvasWordMetrics[] = [];
    wordTokens.forEach((wordToken) => {
        const wordElementMetrics: CanvasWordElementMetrics[] = [];
        wordToken.wordElementTokens.forEach((wordElementToken) => {
            const style = getStyleForMetrics(
                wordElementToken.highlighted,
                normalStyle,
                highlightStyle,
            );
            const metrics = measureWord(wordElementToken.text, style);
            const wordElement = new CanvasWordElementMetrics(
                wordElementToken.text,
                normalStyle,
                highlightStyle,
                metrics,
                wordElementToken,
            );
            if (isNewline(wordElementToken.text)) wordElement.hide();
            wordElementMetrics.push(wordElement);
        });
        wordMetrics.push(new CanvasWordMetrics(wordElementMetrics));
    });
    return wordMetrics;
}

/**
 * Check to see if any text words are too long for a single line and split as necessary. For example, the word
 * "pneumonoultramicroscopicsilicovolcanoconiosis" could get split over multiple lines as "pneumonoultramicroscopic" and
 * "silicovolcanoconiosis" hereby increasing the number of words. Note that we don't keep a reference to the original
 * words.
 */
export function fitWordsToWidth(
    InitWordMetrics: Array<CanvasWordMetrics>,
    wordBreak: boolean,
    maxWidth: number,
    normalStyle: CanvasTextStyleRenderer,
    highlightStyle: CanvasTextStyleRenderer,
): Array<CanvasWordMetrics> {
    if (!wordBreak) {
        return InitWordMetrics;
    }
    return InitWordMetrics.reduce((result, wordMetrics) => {
        const slice = (start, end) => {
            const slicedWordToken = wordMetrics.slice(start, end);
            const slicedTextWordMetrics = createWordMetrics(
                [slicedWordToken],
                normalStyle,
                highlightStyle,
            );
            return slicedTextWordMetrics[0];
        };
        if (wordMetrics.advanceWidth >= maxWidth) {
            let currentStart = 0;
            for (let i = 1; i < wordMetrics.text.length; i++) {
                const thisMetrics = slice(currentStart, i + 1);
                if (thisMetrics.advanceWidth > maxWidth) {
                    result.push(slice(currentStart, i));
                    currentStart = i;
                }
            }
            if (currentStart < wordMetrics.text.length) {
                result.push(slice(currentStart, wordMetrics.text.length));
            }
        } else {
            result.push(wordMetrics);
        }
        return result;
    }, []);
}

/**
 * Assign text words to lines, if word wrap is enabled then fit text words based on the word wrap width
 */
export function assignWordsToLines(
    wordMetricsList: CanvasWordMetrics[],
    lineWidth: number,
): Array<CanvasTextLineMetrics> {
    const lines = [];
    let currentLine = new CanvasTextLineMetrics();
    wordMetricsList.forEach((wordMetrics) => {
        if (isNewline(wordMetrics.text)) {
            currentLine.add(wordMetrics);
            lines.push(currentLine);
            currentLine = new CanvasTextLineMetrics();
        } else {
            if (wordMetrics.advanceWidth + currentLine.advanceWidth > lineWidth) {
                lines.push(currentLine);
                currentLine = new CanvasTextLineMetrics();
            }
            currentLine.add(wordMetrics);
        }
    });
    if (currentLine.children.length > 0) {
        lines.push(currentLine);
    }
    return lines;
}

/**
 * Caps the number of lines based on the maxHeight.
 */
export function capLinesAtMaxHeight(textLineList: CanvasTextLineMetrics[], maxHeight: number) {
    if (textLineList.length === 0) {
        return [];
    }
    const offset = textLineList[0].ascentY;
    const index = textLineList.findIndex((line) => line.descentY - offset > maxHeight);
    return index === -1 ? textLineList : textLineList.splice(index, textLineList.length - index);
}

/**
 * Return the TextMetrics of a word using the given font family and font size.
 */
export function measureWord(word: string, style: CanvasTextStyleRenderer) {
    // NOTE: we only use font size and font family here but we can also specify style (italic) and weight (bold)
    // see https://developer.mozilla.org/en-US/docs/Web/CSS/font for more details
    const ctx = metricsContext();
    style.setupContextWithStyle(ctx, 1);
    return ctx.measureText(word);
}

/** Returns the strings with the largest ascender and descender from the provided array */
export function tallestStrings(array: LargestWordElementsType[]) {
    // get the word elements with the largest ascent
    let largestAscent = 0;
    let largestAscentText = '';

    // get the word elements with the largest descent
    let largestDescent = 0;
    let largestDescentText = '';

    array.forEach((element) => {
        if (element.ascent.value > largestAscent) {
            largestAscent = element.ascent.value;
            largestAscentText = element.ascent.text;
        }
        if (element.descent.value > largestDescent) {
            largestDescent = element.descent.value;
            largestDescentText = element.descent.text;
        }
    });

    const result = [largestAscentText];
    if (largestAscentText !== largestDescentText) {
        result.push(largestDescentText);
    }
    return result;
}

export function tallestStringsFromTranscript(
    transcript: Transcript,
    style: CanvasTextStyleRenderer,
) {
    if (transcript.words.length === 0) return [];

    const words: LargestWordElementsType[] = transcript.words.map((transcriptWordData) => {
        const { actualBoundingBoxAscent: ascent, actualBoundingBoxDescent: descent } = measureWord(
            transcriptWordData.word,
            style,
        );

        return {
            ascent: {
                text: transcriptWordData.word,
                value: ascent,
            },
            descent: {
                text: transcriptWordData.word,
                value: descent,
            },
        };
    });
    return tallestStrings(words);
}

/** Assign a globally unique index to each word. This allows us to figure out the position */
export function assignGloballyUniqueIndex(wordMetrics: CanvasWordMetrics[]) {
    let index = 0;
    wordMetrics.forEach((word) => {
        word.elements.forEach((element) => {
            element.setGlobalWordElementIndex(index);
            index++;
        });
    });
}

export const generateWordMetricsWhichFitWithinMaxWidth = ({
    words,
    highlightAreas = [],
    sourceWordReferenceIndices,
    normalStyle,
    highlightStyle,
    maxWidth,
    wordBreak,
}: {
    words: string[];
    highlightAreas: Array<[number, number]>;
    sourceWordReferenceIndices: number[];
    normalStyle: CanvasTextStyleRenderer;
    highlightStyle: CanvasTextStyleRenderer;
    maxWidth: number;
    wordBreak: boolean;
}) => {
    const wordTokens = annotateWords(words, highlightAreas, sourceWordReferenceIndices);
    const wordMetrics = createWordMetrics(wordTokens, normalStyle, highlightStyle);
    return fitWordsToWidth(wordMetrics, wordBreak, maxWidth, normalStyle, highlightStyle);
};

/**
 * Builds the metrics for a block of text. The text will be broken down into lines and words. Each word will be broken
 * down into word elements.
 * @param wordBreak - if true, the text will be wrapped to the maxWidth, if false, the text will be truncated at the maxWidth.
 * We should use true when rendering text and false when sizing or fitting text for the best visual results.
 */
export const buildMetricsForText = ({
    text,
    alignment = TextAlignment.LEFT,
    normalStyle,
    highlightStyle,
    highlightAreas = [],
    wordBreak = false,
    maxWidth = Infinity,
    maxHeight = Infinity,
}: {
    text: string;
    alignment?: TextAlignment;
    normalStyle: CanvasTextStyleRenderer;
    highlightStyle: CanvasTextStyleRenderer;
    highlightAreas?: Array<[number, number]>;
    wordBreak?: boolean;
    maxWidth?: number;
    maxHeight?: number;
}) => {
    const words = tokenize(text);

    const wordMetrics = generateWordMetricsWhichFitWithinMaxWidth({
        words,
        highlightAreas,
        sourceWordReferenceIndices: [],
        normalStyle,
        highlightStyle,
        wordBreak,
        maxWidth,
    });

    assignGloballyUniqueIndex(wordMetrics);

    const lineMetrics = assignWordsToLines(wordMetrics, maxWidth);
    lineMetrics.forEach((line) => line.removeLeadingTrailingSpaces(alignment));
    const lineMetricsWithContent = lineMetrics.filter((line) => line.text.length > 0);
    positionLinesBelowEachOther(lineMetricsWithContent, normalStyle);
    setAlignmentAndReadingDirection(lineMetricsWithContent, alignment);
    const linesBefore = lineMetrics.length;
    capLinesAtMaxHeight(lineMetricsWithContent, maxHeight);

    return new CanvasTextMetrics(
        lineMetricsWithContent,
        linesBefore === lineMetricsWithContent.length,
    );
};
